import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { money, moneyPrecise, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Plus, DownloadSimple, PencilSimple, Trash, ArrowLeft, Warning, UploadSimple, Paperclip, Sparkle, ChartLineUp, Buildings, Stack, Receipt, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { LineItemDialog } from "@/components/project/LineItemDialog";
import { ImportDialog } from "@/components/project/ImportDialog";
import { AddFromLibraryDialog } from "@/components/project/AddFromLibraryDialog";
import { ReceiptsDialog } from "@/components/project/ReceiptsDialog";
import { AiBudgetDialog } from "@/components/project/AiBudgetDialog";
import { HardSoftBreakdown } from "@/components/HardSoftBreakdown";
import { HealthGauge, gradeColor } from "@/components/HealthGauge";

const emptyItem = { phase_id: "", category: "", description: "", vendor_id: "", budget_amount: 0, actual_amount: 0, status: "not_started", notes: "" };
const STATUS_LABEL = { not_started: "Not started", in_progress: "In progress", completed: "Completed" };

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyItem);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importRows, setImportRows] = useState(null);
  const [importCommitting, setImportCommitting] = useState(false);
  const [importSaveToLib, setImportSaveToLib] = useState(false);
  const [receiptItem, setReceiptItem] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [applyingInvoice, setApplyingInvoice] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiFile, setAiFile] = useState(null);
  const [aiExtracting, setAiExtracting] = useState(false);
  const [aiRows, setAiRows] = useState(null);
  const [aiAdding, setAiAdding] = useState(false);
  const [aiLibrary, setAiLibrary] = useState([]);
  const [saveToLib, setSaveToLib] = useState(false);
  const [benchmarks, setBenchmarks] = useState({});
  const [sale, setSale] = useState({ sale_price: 0, commission_pct: 5, closing_costs: 0, holding_costs: 0, other_costs: 0 });
  const [savingSale, setSavingSale] = useState(false);
  const [building, setBuilding] = useState({ square_feet: 0, lot_size: 0, stories: 0, bedrooms: 0, bathrooms: 0 });
  const [savingBuilding, setSavingBuilding] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [libItems, setLibItems] = useState([]);
  const [libPhase, setLibPhase] = useState("");
  const [libSel, setLibSel] = useState({});
  const [libApplying, setLibApplying] = useState(false);
  const [alertThreshold, setAlertThreshold] = useState(90);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(() => {
    api.get(`/projects/${id}`).then((r) => setData(r.data)).catch(() => navigate("/projects"));
    api.get("/vendors").then((r) => setVendors(r.data));
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/team/branding").then((r) => setAlertThreshold(r.data.alert_threshold_pct || 90)).catch(() => {});
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sa = data?.project?.sale_analysis;
    if (sa) setSale((s) => ({ ...s, ...sa }));
    const p = data?.project;
    if (p) setBuilding({ square_feet: p.square_feet || 0, lot_size: p.lot_size || 0, stories: p.stories || 0, bedrooms: p.bedrooms || 0, bathrooms: p.bathrooms || 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.project?.id]);

  useEffect(() => {
    if (!data) return;
    const itemId = searchParams.get("item");
    if (!itemId) return;
    const it = data.line_items.find((i) => i.id === itemId);
    if (it) openReceipts(it);
    searchParams.delete("item");
    searchParams.delete("view");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const saveBuilding = async () => {
    setSavingBuilding(true);
    try {
      await api.put(`/projects/${id}/building`, {
        square_feet: Number(building.square_feet) || 0,
        lot_size: Number(building.lot_size) || 0,
        stories: Number(building.stories) || 0,
        bedrooms: Number(building.bedrooms) || 0,
        bathrooms: Number(building.bathrooms) || 0,
      });
      toast.success("Building details saved");
      load();
    } catch (err) { toast.error("Could not save building details"); }
    finally { setSavingBuilding(false); }
  };

  const saveSale = async () => {
    setSavingSale(true);
    try {
      await api.put(`/projects/${id}/sale-analysis`, {
        sale_price: Number(sale.sale_price) || 0,
        commission_pct: Number(sale.commission_pct) || 0,
        closing_costs: Number(sale.closing_costs) || 0,
        holding_costs: Number(sale.holding_costs) || 0,
        other_costs: Number(sale.other_costs) || 0,
      });
      toast.success("Profit analysis saved");
    } catch (err) { toast.error("Could not save analysis"); }
    finally { setSavingSale(false); }
  };

  const openNew = (phaseId) => {
    setEditing(null);
    setForm({ ...emptyItem, phase_id: phaseId });
    setOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      phase_id: item.phase_id, category: item.category, description: item.description,
      vendor_id: item.vendor_id || "", budget_amount: item.budget_amount, actual_amount: item.actual_amount,
      status: item.status, notes: item.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, budget_amount: Number(form.budget_amount), actual_amount: Number(form.actual_amount), vendor_id: form.vendor_id === "none" ? "" : form.vendor_id };
    try {
      if (editing) await api.put(`/line-items/${editing}`, payload);
      else await api.post(`/projects/${id}/line-items`, payload);
      toast.success(editing ? "Line item updated" : "Line item added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const removeItem = async (itemId) => {
    await api.delete(`/line-items/${itemId}`);
    toast.success("Line item deleted");
    load();
  };

  const exportExcel = async () => {
    try {
      const res = await api.get(`/projects/${id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.project.name.replace(/ /g, "_")}_cost_report.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel report downloaded");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const downloadTaxReport = async (fmt) => {
    try {
      const res = await api.get(`/reports/tax-report.${fmt}?project_id=${id}`, { responseType: "blob" });
      const mime = fmt === "pdf" ? "application/pdf" : "text/csv";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.project.name.replace(/ /g, "_")}_taxes_paid.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error("Could not download tax report"); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/projects/${id}/import-template.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "line_items_template.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error("Template download failed"); }
  };

  const exportBudget = async (fmt = "csv") => {
    try {
      const res = await api.get(`/projects/${id}/budget-export.${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `budget_export.${fmt}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error("Export failed"); }
  };

  const previewImport = async () => {
    if (!importFile) { toast.error("Choose a CSV or Excel file"); return; }
    setImporting(true);
    const fd = new FormData();
    fd.append("file", importFile);
    try {
      const [{ data }, benchRes] = await Promise.all([
        api.post(`/projects/${id}/import-line-items/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } }),
        api.get("/cost-benchmarks").catch(() => ({ data: {} })),
      ]);
      setBenchmarks(benchRes.data || {});
      if (!data.rows?.length) { toast.message("No rows found in the file"); return; }
      setImportRows(data.rows);
      const matched = data.rows.filter((r) => r.auto_matched).length;
      toast.success(`Parsed ${data.rows.length} rows${matched ? ` · ${matched} auto-matched` : ""} — review before adding`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setImporting(false); }
  };

  const setImportRow = (idx, key, val) =>
    setImportRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  const removeImportRow = (idx) => setImportRows((prev) => prev.filter((_, i) => i !== idx));
  const resetImport = () => { setImportRows(null); setImportFile(null); setImportSaveToLib(false); };

  const commitImport = async () => {
    const kept = (importRows || []).filter((r) => r.description || r.category);
    const rows = kept.map((r) => ({ phase: r.phase, category: r.category, description: r.description, vendor: r.vendor, budget: Number(r.budget) || 0, actual: Number(r.actual) || 0, status: r.status || "not_started" }));
    if (!rows.length) { toast.error("Nothing to add"); return; }
    setImportCommitting(true);
    try {
      const { data } = await api.post(`/projects/${id}/line-items/bulk`, { rows });
      toast.success(`Imported ${data.imported} line item${data.imported === 1 ? "" : "s"}`);
      if (importSaveToLib) {
        await Promise.all(kept.map((r) => api.post("/cost-library?merge=true", {
          category: r.category || "General", description: r.description || "", unit: "",
          default_budget: Number(r.budget) || 0, vendor_id: "",
        }).catch(() => null)));
        toast.success(`Saved ${kept.length} item${kept.length === 1 ? "" : "s"} to Cost Library`);
      }
      setImportOpen(false);
      resetImport();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setImportCommitting(false); }
  };

  const openLibrary = async () => {
    setLibPhase("");
    setLibSel({});
    try {
      const { data: items } = await api.get("/cost-library");
      setLibItems(items);
    } catch { setLibItems([]); }
    setLibOpen(true);
  };
  const toggleLib = (lid) => setLibSel((s) => {
    const n = { ...s };
    if (n[lid] != null) delete n[lid]; else n[lid] = 1;
    return n;
  });
  const setLibQty = (lid, q) => setLibSel((s) => ({ ...s, [lid]: q }));
  const applyLibrary = async () => {
    if (!libPhase) { toast.error("Select a phase"); return; }
    const items = Object.entries(libSel).map(([library_id, quantity]) => ({ library_id, quantity: Number(quantity) || 1 }));
    if (!items.length) { toast.error("Select at least one item"); return; }
    setLibApplying(true);
    try {
      const { data: res } = await api.post(`/projects/${id}/apply-library`, { phase_id: libPhase, items });
      toast.success(`Added ${res.imported} item${res.imported === 1 ? "" : "s"} from library`);
      setLibOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLibApplying(false); }
  };

  const openReceipts = async (item) => {
    setReceiptItem(item);
    setReceipts([]);
    setInvoiceResult(null);
    const { data } = await api.get(`/line-items/${item.id}/receipts`);
    setReceipts(data);
  };

  const deleteReceipt = async (rid) => {
    await api.delete(`/receipts/${rid}`);
    setReceipts((prev) => prev.filter((r) => r.id !== rid));
    load();
  };

  const uploadInvoice = async (fileList) => {
    if (!fileList || !fileList[0]) return;
    setUploadingInvoice(true);
    setInvoiceResult(null);
    try {
      const fd = new FormData();
      fd.append("file", fileList[0]);
      const { data } = await api.post(`/line-items/${receiptItem.id}/invoice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInvoiceResult(data);
      const r = await api.get(`/line-items/${receiptItem.id}/receipts`);
      setReceipts(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read the invoice");
    } finally { setUploadingInvoice(false); }
  };

  const applyInvoice = async () => {
    if (!invoiceResult || !receiptItem) return;
    setApplyingInvoice(true);
    const invoices = receipts.filter((r) => r.is_invoice);
    const total = invoices.reduce((s, r) => s + (r.invoice_total || 0), 0) || invoiceResult.total_amount;
    try {
      await api.put(`/line-items/${receiptItem.id}`, {
        phase_id: receiptItem.phase_id || "",
        category: receiptItem.category || "",
        description: receiptItem.description || "",
        vendor_id: invoiceResult.vendor_id || receiptItem.vendor_id || "",
        budget_amount: receiptItem.budget_amount || 0,
        actual_amount: total,
        status: receiptItem.status || "not_started",
        notes: receiptItem.notes || "",
      });
      toast.success(`Actual cost set to ${money(total)}${invoiceResult.vendor_matched ? " · vendor linked" : ""}`);
      setInvoiceResult(null);
      setReceiptItem(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setApplyingInvoice(false); }
  };

  const openAi = () => { setAiOpen(true); setAiFile(null); setAiRows(null); setSaveToLib(false); };

  const runExtract = async () => {
    if (!aiFile) { toast.error("Choose a PDF or image"); return; }
    setAiExtracting(true);
    const fd = new FormData();
    fd.append("file", aiFile);
    try {
      const [{ data }, benchRes, libRes] = await Promise.all([
        api.post("/ai/extract-budget", fd, { headers: { "Content-Type": "multipart/form-data" } }),
        api.get("/cost-benchmarks").catch(() => ({ data: {} })),
        api.get("/cost-library").catch(() => ({ data: [] })),
      ]);
      setBenchmarks(benchRes.data || {});
      setAiLibrary(libRes.data || []);
      setAiRows(data.items || []);
      if (!data.items?.length) toast.message(data.notes || "No line items found in this document");
      else toast.success(`AI found ${data.items.length} line items`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setAiExtracting(false); }
  };

  const libKey = (c, d) => `${(c || "").trim().toLowerCase()}|${(d || "").trim().toLowerCase()}`;
  const inLibrary = (r) => aiLibrary.some((l) => libKey(l.category, l.description) === libKey(r.category, r.description));
  const libHint = (r) => {
    const b = benchmarks[(r.category || "").trim().toLowerCase()];
    return b && b.count ? `seen ${b.count}× · avg ${money(b.avg)}` : null;
  };
  const saveRowToLibrary = async (r) => {
    if (!r.category && !r.description) { toast.error("Category or description required"); return; }
    try {
      const { data: created } = await api.post("/cost-library?merge=true", { category: r.category || "General", description: r.description || "", unit: "", default_budget: Number(r.budget) || 0, vendor_id: "" });
      setAiLibrary((prev) => [...prev, created]);
      toast.success("Saved to Cost Library");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const flagInfo = (row) => {
    const key = (row.category || "").trim().toLowerCase();
    const b = benchmarks[key];
    if (!b || b.count < 2) return null;
    const amt = Number(row.budget) || 0;
    if (amt > b.avg * 1.4) {
      return { pct: Math.round((amt / b.avg - 1) * 100), avg: b.avg };
    }
    return null;
  };

  const setAiRow = (idx, key, val) => {
    setAiRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  };
  const removeAiRow = (idx) => setAiRows((prev) => prev.filter((_, i) => i !== idx));

  const addAiRows = async () => {
    const rows = aiRows
      .filter((r) => (r.description || r.category))
      .map((r) => ({ phase: r.phase, category: r.category, description: r.description, vendor: r.vendor, budget: Number(r.budget) || 0, actual: 0, status: "not_started" }));
    if (!rows.length) { toast.error("Nothing to add"); return; }
    setAiAdding(true);
    try {
      const { data } = await api.post(`/projects/${id}/line-items/bulk`, { rows });
      toast.success(`Added ${data.imported} line items to budget`);
      if (saveToLib) {
        const newOnes = aiRows.filter((r) => (r.description || r.category) && !inLibrary(r));
        if (newOnes.length) {
          await Promise.all(newOnes.map((r) => api.post("/cost-library?merge=true", { category: r.category || "General", description: r.description || "", unit: "", default_budget: Number(r.budget) || 0, vendor_id: "" }).catch(() => null)));
          toast.success(`Saved ${newOnes.length} new item${newOnes.length === 1 ? "" : "s"} to Cost Library`);
        }
      }
      setAiOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setAiAdding(false); }
  };

  const aiTotal = aiRows ? aiRows.reduce((s, r) => s + (Number(r.budget) || 0), 0) : 0;

  if (!data) return <div className="p-8 label-caps animate-pulse">Loading…</div>;

  const { project, phases, line_items } = data;
  const spentPct = project.total_budget > 0 ? Math.min(100, (project.total_actual / project.total_budget) * 100) : 0;
  const over = project.variance < 0;
  const sqft = Number(project.square_feet) || 0;
  const psf = (amt) => (sqft > 0 ? `$${(amt / sqft).toFixed(0)}/sf` : "—");

  const salePrice = Number(sale.sale_price) || 0;
  const sellingCosts = salePrice * (Number(sale.commission_pct) || 0) / 100 + (Number(sale.closing_costs) || 0) + (Number(sale.holding_costs) || 0) + (Number(sale.other_costs) || 0);
  const costActual = project.total_actual;
  const netProfit = salePrice - costActual - sellingCosts;
  const netProfitBudget = salePrice - project.total_budget - sellingCosts;
  const roi = costActual > 0 ? (netProfit / costActual) * 100 : 0;
  const margin = salePrice > 0 ? (netProfit / salePrice) * 100 : 0;
  const profitable = netProfit >= 0;

  const groupOf = (cat) => {
    const c = categories.find((x) => x.name.toLowerCase() === (cat || "").trim().toLowerCase());
    const g = c?.group;
    return g === "Hard Costs" || g === "Soft Costs" ? g : "Unclassified";
  };
  const quotedByCat = data.quoted_by_category || {};
  const quotedForCat = (cat) => {
    const key = Object.keys(quotedByCat).find((k) => k.toLowerCase() === (cat || "").trim().toLowerCase());
    return key ? quotedByCat[key] : 0;
  };
  const byGroup = ["Hard Costs", "Soft Costs", "Unclassified"].map((g) => {
    const its = line_items.filter((i) => groupOf(i.category) === g);
    const cats = [...new Set(its.map((i) => i.category))];
    return {
      group: g,
      budget: its.reduce((s, i) => s + i.budget_amount, 0),
      actual: its.reduce((s, i) => s + i.actual_amount, 0),
      quoted: cats.reduce((s, c) => s + quotedForCat(c), 0),
    };
  });
  const byCategory = Object.values(line_items.reduce((acc, i) => {
    const key = i.category || "Uncategorized";
    acc[key] = acc[key] || { category: key, group: groupOf(i.category), budget: 0, actual: 0, quoted: quotedForCat(key) };
    acc[key].budget += i.budget_amount;
    acc[key].actual += i.actual_amount;
    return acc;
  }, {}));

  return (
    <div>
      <PageHeader label={`${project.type} · ${project.status.replace("_", " ")}`} title={project.name}
        accent={project.health ? gradeColor(project.health.grade) : undefined}>
        <Button variant="outline" onClick={() => navigate("/projects")} className="rounded-none" data-testid="back-button">
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
        <Button onClick={openAi} className="rounded-none" data-testid="ai-budget-button">
          <Sparkle size={16} weight="fill" className="mr-1" /> AI Budget
        </Button>
        <Button variant="outline" onClick={openLibrary} className="rounded-none" data-testid="add-from-library-button">
          <Stack size={16} className="mr-1" /> Add from Library
        </Button>
        <Button variant="outline" onClick={() => setImportOpen(true)} className="rounded-none" data-testid="import-button">
          <UploadSimple size={16} className="mr-1" /> Import
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="rounded-none" data-testid="export-budget-button">
              <DownloadSimple size={16} className="mr-1" /> Export Budget
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-none w-56">
            <DropdownMenuLabel className="label-caps text-xs">Editable budget (re-importable)</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => exportBudget("csv")} data-testid="export-budget-csv-item" className="cursor-pointer">
              <DownloadSimple size={16} className="mr-2" /> Download CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportBudget("xlsx")} data-testid="export-budget-xlsx-item" className="cursor-pointer">
              <DownloadSimple size={16} className="mr-2" /> Download Excel (.xlsx)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" onClick={exportExcel} className="rounded-none" data-testid="export-excel-button">
          <DownloadSimple size={16} className="mr-1" /> Export Excel
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="rounded-none" data-testid="tax-report-button">
              <Receipt size={16} className="mr-1" /> Tax report
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-none w-52">
            <DropdownMenuLabel className="label-caps text-xs">Taxes paid (this project)</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => downloadTaxReport("pdf")} data-testid="tax-report-pdf" className="cursor-pointer">
              <FilePdf size={16} className="mr-2" /> Download PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => downloadTaxReport("csv")} data-testid="tax-report-csv" className="cursor-pointer">
              <DownloadSimple size={16} className="mr-2" /> Download CSV
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      <div className="p-4 sm:p-8">
        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-border mb-8">
          <div className="border-r border-b border-border p-6">
            <p className="label-caps mb-2">Budget</p>
            <div className="font-mono-fig text-2xl font-semibold tabular-nums">{money(project.total_budget)}</div>
            {sqft > 0 && <div className="text-xs text-muted-foreground mt-1">{psf(project.total_budget)}</div>}
          </div>
          <div className="border-r border-b border-border p-6">
            <p className="label-caps mb-2">Actual</p>
            <div className="font-mono-fig text-2xl font-semibold tabular-nums">{money(project.total_actual)}</div>
            {sqft > 0 && <div className="text-xs text-muted-foreground mt-1">{psf(project.total_actual)}</div>}
          </div>
          <div className="border-r border-b border-border p-6">
            <p className="label-caps mb-2">Variance</p>
            <div className={`font-mono-fig text-2xl font-semibold tabular-nums ${over ? "text-destructive" : "text-emerald-700"}`}>
              {over ? "-" : ""}{money(Math.abs(project.variance))}
            </div>
          </div>
          <div className="border-r border-b border-border p-6">
            <p className="label-caps mb-2">Spent</p>
            <div className="font-mono-fig text-2xl font-semibold tabular-nums">{spentPct.toFixed(0)}%</div>
            <Progress value={spentPct} className="mt-2 h-1.5 rounded-none" />
          </div>
        </div>

        <HealthGauge health={project.health} />

        {/* Building Details */}
        <div className="border border-border mb-8" data-testid="building-details">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary">
            <Buildings size={18} weight="bold" />
            <span className="font-heading font-bold">Building Details</span>
            {sqft > 0 && <span className="text-xs text-muted-foreground ml-1">{sqft.toLocaleString()} sq ft · {psf(project.total_budget)} budgeted</span>}
          </div>
          <div className="p-4 sm:p-6 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <Label className="label-caps">Square Feet</Label>
              <Input type="number" value={building.square_feet} onChange={(e) => setBuilding({ ...building, square_feet: e.target.value })} data-testid="building-sqft-input" className="mt-2 font-mono-fig" />
            </div>
            <div>
              <Label className="label-caps">Lot Size (sf)</Label>
              <Input type="number" value={building.lot_size} onChange={(e) => setBuilding({ ...building, lot_size: e.target.value })} data-testid="building-lot-input" className="mt-2 font-mono-fig" />
            </div>
            <div>
              <Label className="label-caps">Stories</Label>
              <Input type="number" value={building.stories} onChange={(e) => setBuilding({ ...building, stories: e.target.value })} className="mt-2 font-mono-fig" />
            </div>
            <div>
              <Label className="label-caps">Beds</Label>
              <Input type="number" value={building.bedrooms} onChange={(e) => setBuilding({ ...building, bedrooms: e.target.value })} className="mt-2 font-mono-fig" />
            </div>
            <div>
              <Label className="label-caps">Baths</Label>
              <Input type="number" value={building.bathrooms} onChange={(e) => setBuilding({ ...building, bathrooms: e.target.value })} className="mt-2 font-mono-fig" />
            </div>
            <div className="col-span-2 md:col-span-5">
              <Button onClick={saveBuilding} disabled={savingBuilding} variant="outline" className="rounded-none" data-testid="save-building">
                {savingBuilding ? "Saving…" : "Save Building Details"}
              </Button>
            </div>
          </div>
        </div>

        {over && (
          <div data-testid="project-over-budget-alert" className="flex items-center gap-2 border border-destructive text-destructive px-4 py-3 mb-8 text-sm">
            <Warning size={18} weight="bold" /> This project is over budget by {money(Math.abs(project.variance))}.
          </div>
        )}

        {/* Profit Analysis */}
        <div className="border border-border mb-8" data-testid="profit-analysis">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary">
            <ChartLineUp size={18} weight="bold" />
            <span className="font-heading font-bold">Profit Analysis — If Sold</span>
          </div>
          <div className="grid md:grid-cols-2">
            {/* Inputs */}
            <div className="p-6 space-y-4 border-b md:border-b-0 md:border-r border-border">
              <div>
                <Label className="label-caps">Expected Sale Price</Label>
                <Input type="number" value={sale.sale_price} onChange={(e) => setSale({ ...sale, sale_price: e.target.value })}
                  data-testid="sale-price-input" className="mt-2 font-mono-fig" placeholder="0" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-caps">Agent Commission (%)</Label>
                  <Input type="number" step="0.1" value={sale.commission_pct} onChange={(e) => setSale({ ...sale, commission_pct: e.target.value })}
                    data-testid="commission-input" className="mt-2 font-mono-fig" />
                </div>
                <div>
                  <Label className="label-caps">Closing Costs</Label>
                  <Input type="number" value={sale.closing_costs} onChange={(e) => setSale({ ...sale, closing_costs: e.target.value })}
                    data-testid="closing-costs-input" className="mt-2 font-mono-fig" />
                </div>
                <div>
                  <Label className="label-caps">Holding Costs</Label>
                  <Input type="number" value={sale.holding_costs} onChange={(e) => setSale({ ...sale, holding_costs: e.target.value })}
                    data-testid="holding-costs-input" className="mt-2 font-mono-fig" />
                </div>
                <div>
                  <Label className="label-caps">Other Selling Costs</Label>
                  <Input type="number" value={sale.other_costs} onChange={(e) => setSale({ ...sale, other_costs: e.target.value })}
                    data-testid="other-costs-input" className="mt-2 font-mono-fig" />
                </div>
              </div>
              <Button onClick={saveSale} disabled={savingSale} className="rounded-none w-full" data-testid="save-sale-analysis">
                {savingSale ? "Saving…" : "Save Analysis"}
              </Button>
            </div>

            {/* Results */}
            <div className="p-6">
              <div className="space-y-3 text-sm">
                <Row label="Sale Price" value={money(salePrice)} />
                <Row label="Selling Costs" value={`- ${money(sellingCosts)}`} muted />
                <Row label={`Build Cost (actual, ${money(project.total_actual)})`} value={`- ${money(costActual)}`} muted />
                <div className="border-t border-border my-2" />
                <div className="flex items-center justify-between">
                  <span className="label-caps">Net Profit</span>
                  <span className={`font-mono-fig text-2xl font-bold ${profitable ? "text-emerald-700" : "text-destructive"}`} data-testid="net-profit">
                    {netProfit < 0 ? "-" : ""}{money(Math.abs(netProfit))}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="border border-border p-3">
                    <div className="label-caps mb-1">ROI</div>
                    <div className={`font-mono-fig text-lg font-semibold ${roi >= 0 ? "" : "text-destructive"}`} data-testid="roi">{roi.toFixed(1)}%</div>
                  </div>
                  <div className="border border-border p-3">
                    <div className="label-caps mb-1">Profit Margin</div>
                    <div className={`font-mono-fig text-lg font-semibold ${margin >= 0 ? "" : "text-destructive"}`} data-testid="margin">{margin.toFixed(1)}%</div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground pt-2">
                  Vs. budgeted cost, net profit would be{" "}
                  <span className={netProfitBudget >= 0 ? "text-emerald-700 font-medium" : "text-destructive font-medium"}>
                    {netProfitBudget < 0 ? "-" : ""}{money(Math.abs(netProfitBudget))}
                  </span>.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Hard vs Soft cost split */}
        <div className="mb-8">
          <HardSoftBreakdown data={byGroup} categories={byCategory} projectId={id} testId="project-hard-soft" />
        </div>

        {/* Phases */}
        <div className="space-y-8">
          {phases.map((phase) => {
            const items = line_items.filter((i) => i.phase_id === phase.id);
            const pB = items.reduce((s, i) => s + i.budget_amount, 0);
            const pA = items.reduce((s, i) => s + i.actual_amount, 0);
            return (
              <div key={phase.id} className="border border-border" data-testid={`phase-${phase.id}`}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary">
                  <div className="flex items-center gap-3">
                    <span className="font-heading font-bold">{phase.name}</span>
                    <span className="text-xs text-muted-foreground font-mono-fig">
                      {money(pA)} / {money(pB)}
                    </span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => openNew(phase.id)}
                    className="rounded-none h-8" data-testid={`add-item-${phase.id}`}>
                    <Plus size={14} className="mr-1" /> Add cost
                  </Button>
                </div>
                {items.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-muted-foreground">No line items in this phase.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>Category</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Budget</TableHead>
                        <TableHead className="text-right">Actual</TableHead>
                        <TableHead className="text-right">Variance</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((i) => {
                        const v = i.budget_amount - i.actual_amount;
                        const over = i.actual_amount > i.budget_amount && i.budget_amount > 0;
                        const approaching = !over && i.budget_amount > 0 && i.actual_amount >= i.budget_amount * (alertThreshold / 100);
                        return (
                          <TableRow key={i.id} data-testid={`line-item-${i.id}`} className={`transition-colors duration-200 ${over ? "bg-destructive/5 hover:bg-destructive/10" : approaching ? "bg-amber-50 hover:bg-amber-100/60" : "hover:bg-secondary"}`}>
                            <TableCell className="font-medium">
                              <span className="flex items-center gap-1.5">
                                {i.category}
                                {over && <span data-testid={`over-badge-${i.id}`} className="text-[10px] px-1.5 py-0.5 border border-destructive text-destructive font-semibold">OVER</span>}
                                {approaching && <span data-testid={`approaching-badge-${i.id}`} title={`Actual is ${Math.round(i.actual_amount / i.budget_amount * 100)}% of budget (alert at ${alertThreshold}%)`} className="text-[10px] px-1.5 py-0.5 border border-amber-500 text-amber-600 font-semibold">{Math.round(i.actual_amount / i.budget_amount * 100)}%</span>}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate">{i.description}</TableCell>
                            <TableCell className="text-sm">{i.vendor_name || "—"}</TableCell>
                            <TableCell className="text-xs">{STATUS_LABEL[i.status]}</TableCell>
                            <TableCell className="text-right font-mono-fig tabular-nums">
                              {money(i.budget_amount)}
                              {sqft > 0 && <div className="text-[10px] text-muted-foreground">{psf(i.budget_amount)}</div>}
                            </TableCell>
                            <TableCell className="text-right font-mono-fig tabular-nums">{money(i.actual_amount)}</TableCell>
                            <TableCell className={`text-right font-mono-fig tabular-nums ${v < 0 ? "text-destructive" : "text-emerald-700"}`}>
                              {v < 0 ? "-" : ""}{money(Math.abs(v))}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              <button onClick={() => openReceipts(i)} data-testid={`receipts-item-${i.id}`} title="Invoices & Receipts" className="relative text-muted-foreground hover:text-primary mr-3">
                                <Paperclip size={16} />
                                {i.receipt_count > 0 && <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[10px] font-mono-fig rounded-full w-4 h-4 flex items-center justify-center">{i.receipt_count}</span>}
                              </button>
                              <button onClick={() => openEdit(i)} data-testid={`edit-item-${i.id}`} className="text-muted-foreground hover:text-primary mr-3"><PencilSimple size={16} /></button>
                              <button onClick={() => removeItem(i.id)} data-testid={`delete-item-${i.id}`} className="text-muted-foreground hover:text-destructive"><Trash size={16} /></button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <LineItemDialog open={open} setOpen={setOpen} editing={editing} form={form} setForm={setForm}
        phases={phases} vendors={vendors} categories={categories} submit={submit} saving={saving} />

      <ImportDialog importOpen={importOpen} setImportOpen={setImportOpen} importFile={importFile}
        setImportFile={setImportFile} importing={importing} previewImport={previewImport}
        importRows={importRows} setImportRow={setImportRow} removeImportRow={removeImportRow}
        commitImport={commitImport} importCommitting={importCommitting} resetImport={resetImport}
        importSaveToLib={importSaveToLib} setImportSaveToLib={setImportSaveToLib}
        categories={categories} money={money} benchmarks={benchmarks}
        downloadTemplate={downloadTemplate} exportBudgetCsv={() => exportBudget("csv")} />

      <AddFromLibraryDialog libOpen={libOpen} setLibOpen={setLibOpen} phases={data.phases || []}
        libItems={libItems} libPhase={libPhase} setLibPhase={setLibPhase} libSel={libSel}
        toggleLib={toggleLib} setLibQty={setLibQty} applyLibrary={applyLibrary} libApplying={libApplying}
        navigate={navigate} money={money} moneyPrecise={moneyPrecise} />

      <ReceiptsDialog receiptItem={receiptItem} onClose={() => setReceiptItem(null)} receipts={receipts}
        uploadInvoice={uploadInvoice} uploadingInvoice={uploadingInvoice} invoiceResult={invoiceResult}
        applyInvoice={applyInvoice} applying={applyingInvoice} deleteReceipt={deleteReceipt}
        BACKEND={BACKEND} money={money} />

      <AiBudgetDialog aiOpen={aiOpen} setAiOpen={setAiOpen} aiRows={aiRows} setAiRows={setAiRows}
        aiFile={aiFile} setAiFile={setAiFile} aiExtracting={aiExtracting} runExtract={runExtract}
        flagInfo={flagInfo} inLibrary={inLibrary} libHint={libHint} saveRowToLibrary={saveRowToLibrary}
        setAiRow={setAiRow} removeAiRow={removeAiRow} addAiRows={addAiRows} aiAdding={aiAdding}
        saveToLib={saveToLib} setSaveToLib={setSaveToLib} aiTotal={aiTotal} money={money} categories={categories} />
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className={muted ? "text-muted-foreground" : ""}>{label}</span>
      <span className={`font-mono-fig ${muted ? "text-muted-foreground" : "font-medium"}`}>{value}</span>
    </div>
  );
}
